import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Eraser, GripHorizontal, StickyNote, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  loadWantedMonthlyMemoPanelOpen,
  loadWantedMonthlyMemoPanelPosition,
  saveWantedMonthlyMemoPanelOpen,
  saveWantedMonthlyMemoPanelPosition,
  type WantedMonthlyMemoPanelPosition,
} from "./wantedMonthlyMemoPanelPosition";

type WantedMonthlyMemoProps = Readonly<{
  year: number;
  month: number;
  groupId?: string;
  memo?: string | null;
  canEdit: boolean;
  nurseId?: string;
  isMemoLoading?: boolean;
  isMemoError?: boolean;
  onSave?: (memo: string) => Promise<void>;
  onRetryLoad?: () => void;
}>;

type PanelPosition = WantedMonthlyMemoPanelPosition;

const VIEWPORT_GUTTER = 12;
const KEYBOARD_MOVE_DISTANCE = 24;
const AUTO_SAVE_DELAY_MS = 800;

export function WantedMonthlyMemo({
  year,
  month,
  groupId,
  memo,
  canEdit,
  nurseId,
  isMemoLoading = false,
  isMemoError = false,
  onSave,
  onRetryLoad,
}: WantedMonthlyMemoProps) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragOffsetRef = useRef<PanelPosition | null>(null);
  const scopeKey = `${year}-${month}-${groupId ?? ""}`;
  const resolvedScopeRef = useRef<string | null>(null);
  const currentScopeRef = useRef(scopeKey);
  const [initialPanelPosition] = useState(() =>
    loadWantedMonthlyMemoPanelPosition(nurseId)
  );
  const [initialIsOpen] = useState(() =>
    loadWantedMonthlyMemoPanelOpen(nurseId)
  );
  const hasPanelPositionRef = useRef(initialPanelPosition !== null);
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [savedMemo, setSavedMemo] = useState(memo ?? "");
  const [draft, setDraft] = useState(memo ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [position, setPosition] = useState<PanelPosition>(
    initialPanelPosition ?? { x: VIEWPORT_GUTTER, y: 96 }
  );
  const hasMemo = draft.trim().length > 0;
  const memoActionLabel = isOpen ? "원티드 메모 닫기" : "원티드 메모 열기";

  const hasResolvedCurrentScope = resolvedScopeRef.current === scopeKey;
  const isInitialMemoLoading = isMemoLoading && !hasResolvedCurrentScope;
  const hasInitialMemoError = isMemoError && !hasResolvedCurrentScope;
  const hasUnsavedChanges = draft !== savedMemo;

  useEffect(() => {
    currentScopeRef.current = scopeKey;
  }, [scopeKey]);

  useEffect(() => {
    if (isMemoLoading || isMemoError || hasResolvedCurrentScope) return;

    const nextMemo = memo ?? "";
    resolvedScopeRef.current = scopeKey;
    setSavedMemo(nextMemo);
    setDraft(nextMemo);
    setSaveError(false);
  }, [hasResolvedCurrentScope, isMemoError, isMemoLoading, memo, scopeKey]);

  const saveDraft = useCallback(
    async (memoToSave: string) => {
      if (!onSave) return;

      const requestScope = scopeKey;
      setIsSaving(true);
      setSaveError(false);
      try {
        await onSave(memoToSave);
        if (currentScopeRef.current === requestScope) {
          setSavedMemo(memoToSave);
        }
      } catch {
        if (currentScopeRef.current === requestScope) {
          setSaveError(true);
        }
      } finally {
        if (currentScopeRef.current === requestScope) {
          setIsSaving(false);
        }
      }
    },
    [onSave, scopeKey]
  );

  useEffect(() => {
    if (
      !canEdit ||
      !onSave ||
      isInitialMemoLoading ||
      isSaving ||
      saveError ||
      !hasUnsavedChanges
    )
      return;

    const timer = window.setTimeout(() => {
      void saveDraft(draft);
    }, AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [
    canEdit,
    draft,
    hasUnsavedChanges,
    isInitialMemoLoading,
    isSaving,
    onSave,
    saveDraft,
    saveError,
  ]);

  useEffect(() => {
    if (!hasPanelPositionRef.current) return;

    saveWantedMonthlyMemoPanelPosition(nurseId, position);
  }, [nurseId, position]);

  const clampPosition = (nextPosition: PanelPosition): PanelPosition => {
    const panel = panelRef.current;
    const width = panel?.offsetWidth ?? Math.min(368, window.innerWidth - 24);
    const height = panel?.offsetHeight ?? 300;

    return {
      x: Math.min(
        Math.max(nextPosition.x, VIEWPORT_GUTTER),
        Math.max(VIEWPORT_GUTTER, window.innerWidth - width - VIEWPORT_GUTTER)
      ),
      y: Math.min(
        Math.max(nextPosition.y, VIEWPORT_GUTTER),
        Math.max(VIEWPORT_GUTTER, window.innerHeight - height - VIEWPORT_GUTTER)
      ),
    };
  };

  const setPanelOpen = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    saveWantedMonthlyMemoPanelOpen(nurseId, nextIsOpen);
  };

  const closePanel = () => {
    setPanelOpen(false);
  };

  const openPanel = () => {
    if (hasPanelPositionRef.current) {
      setPanelOpen(true);
      requestAnimationFrame(() => {
        setPosition((currentPosition) => clampPosition(currentPosition));
      });
      return;
    }

    const triggerBounds = triggerRef.current?.getBoundingClientRect();
    const isMobile = window.matchMedia("(max-width: 639px)").matches;

    hasPanelPositionRef.current = true;
    setPosition(
      clampPosition({
        x: isMobile
          ? VIEWPORT_GUTTER
          : triggerBounds?.left ?? VIEWPORT_GUTTER,
        y: isMobile
          ? window.innerHeight - 320
          : (triggerBounds?.bottom ?? 84) + VIEWPORT_GUTTER,
      })
    );
    setPanelOpen(true);
  };

  const handleClear = () => {
    setSaveError(false);
    setDraft("");
  };

  const handleDraftChange = (value: string) => {
    setSaveError(false);
    setDraft(value);
  };

  const status = isInitialMemoLoading
    ? "메모를 불러오는 중…"
    : hasInitialMemoError
      ? "메모를 불러오지 못했습니다."
      : isSaving
        ? "저장 중…"
        : saveError
          ? "메모를 저장하지 못했습니다."
          : hasUnsavedChanges
            ? "변경사항 있음"
            : savedMemo.trim()
              ? "저장됨"
              : canEdit
                ? "메모를 적으면 자동 저장됩니다."
                : "등록된 메모가 없습니다.";

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setIsOpen(false);
      saveWantedMonthlyMemoPanelOpen(nurseId, false);
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, nurseId]);

  const handleDragStart = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    dragOffsetRef.current = {
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragOffset = dragOffsetRef.current;
    if (!dragOffset) return;
    setPosition(
      clampPosition({
        x: event.clientX - dragOffset.x,
        y: event.clientY - dragOffset.y,
      })
    );
  };

  const handleDragEnd = () => {
    dragOffsetRef.current = null;
  };

  const handleDragKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const movementByKey: Record<string, PanelPosition> = {
      ArrowDown: { x: 0, y: KEYBOARD_MOVE_DISTANCE },
      ArrowUp: { x: 0, y: -KEYBOARD_MOVE_DISTANCE },
      ArrowLeft: { x: -KEYBOARD_MOVE_DISTANCE, y: 0 },
      ArrowRight: { x: KEYBOARD_MOVE_DISTANCE, y: 0 },
    };
    const movement = movementByKey[event.key];
    if (!movement) return;

    event.preventDefault();
    setPosition((currentPosition) =>
      clampPosition({
        x: currentPosition.x + movement.x,
        y: currentPosition.y + movement.y,
      })
    );
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            size="icon"
            className={`relative border-border bg-background text-text-main2 hover:bg-light-muted ${
              isOpen
                ? "border-amber-400 text-amber-900 shadow-sm hover:bg-light-muted dark:border-amber-700 dark:text-amber-200 dark:hover:bg-light-muted"
                : ""
            }`}
            aria-pressed={isOpen}
            aria-expanded={isOpen}
            aria-controls={panelId}
            aria-label={memoActionLabel}
            onClick={() => (isOpen ? closePanel() : openPanel())}
          >
            <StickyNote aria-hidden="true" />
            {hasMemo && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-rose-300 ring-2 ring-background dark:bg-rose-400"
              />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          {memoActionLabel}
        </TooltipContent>
      </Tooltip>

      {isOpen &&
        createPortal(
        <section
          ref={panelRef}
          id={panelId}
          aria-label={`${year}년 ${month}월 원티드 메모`}
          className="fixed z-30 w-[calc(100vw-1.5rem)] max-w-[23rem] overflow-hidden rounded-lg border border-amber-200 bg-amber-50 shadow-lg sm:w-[23rem] dark:border-amber-900/70 dark:bg-amber-950/35"
          style={{ left: position.x, top: position.y }}
        >
          <div className="flex items-center justify-between gap-2 border-b border-amber-200/80 px-3 py-2 dark:border-amber-900/70">
            <button
              type="button"
              className="-ml-1 inline-flex min-w-0 flex-1 touch-none cursor-grab items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm font-semibold text-text-main2 outline-none focus-visible:ring-2 focus-visible:ring-sig active:cursor-grabbing"
              aria-label="메모 창 이동. 화살표 키로 이동할 수 있습니다."
              onPointerDown={handleDragStart}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
              onKeyDown={handleDragKeyDown}
            >
              <GripHorizontal aria-hidden="true" className="size-4 shrink-0 text-text-sub" />
              <span className="truncate">원티드 메모</span>
              <span className="shrink-0 text-xs font-normal text-text-sub">
                {year}년 {month}월
              </span>
            </button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="원티드 메모 닫기"
              onClick={closePanel}
            >
              <X aria-hidden="true" />
            </Button>
          </div>
          <div className="space-y-2 p-3">
            <Textarea
              value={draft}
              readOnly={!canEdit || isInitialMemoLoading}
              disabled={isInitialMemoLoading}
              aria-label="이번 달 메모 내용"
              className="min-h-28 resize-y border-amber-200 bg-background/85 leading-6 shadow-none placeholder:text-text-sub placeholder:opacity-60 focus-visible:border-sig dark:border-amber-900/70"
              placeholder="메모를 적어주세요."
              onChange={(event) => handleDraftChange(event.target.value)}
            />
            <div className="flex min-h-8 items-center justify-between gap-2">
              <p
                aria-live="polite"
                className={`min-w-0 text-xs ${
                  hasInitialMemoError || saveError
                    ? "text-destructive"
                    : "text-text-sub"
                }`}
              >
                {status}
              </p>
              {(hasInitialMemoError || saveError) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 shrink-0 px-2 text-xs"
                  onClick={() => {
                    if (hasInitialMemoError) {
                      onRetryLoad?.();
                      return;
                    }
                    void saveDraft(draft);
                  }}
                >
                  다시 시도
                </Button>
              )}
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:text-amber-200 dark:hover:bg-amber-900/50 dark:hover:text-amber-100"
                  aria-label="메모 비우기"
                  title="메모 비우기"
                  disabled={isSaving || !draft.trim()}
                  onClick={handleClear}
                >
                  <Eraser aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        </section>,
          document.body
        )}
    </>
  );
}
